// The Phase 5 memory taxonomy (docs/architecture/phase-5-memory.md, section 2 and 3): what each kind of
// memory may hold, how sensitive it is, whether a model may promote it without a human, and how long it
// may live. Everything here is data and pure functions. A change to a list, a class or a number is a new
// memory policy version (`MEMORY_POLICY_VERSION` in `memory-policy.ts`), never a silent edit.
/** Why a candidate waits for a human. The database CHECK `customer_memory_facts_review_reason_valid` is generated from this tuple. */
export const memoryReviewReasons = Object.freeze([
    "INFERRED",
    "PERSONAL_NEEDS_REMEMBER",
    "KIND_REQUIRES_REVIEW",
    "FREE_TEXT",
    "LOW_CONFIDENCE",
    "RECENT_CUSTOMER_DELETION",
    "UPDATE_PROPOSED",
    "CONFLICT",
    "CONFLICTS_WITH_CANDIDATE",
    "SCOPE_AMBIGUOUS",
]);
/** Why a fact has an evidence row. Evidence is an id and an event, never text. */
export const memoryEvidenceEvents = Object.freeze([
    "ORIGIN",
    "REINFORCE",
    "RETRACTION",
    "SOURCE_EDITED",
    "SOURCE_DELETED",
]);
/** What started an extraction run (the run row, `memory_extraction_runs`). */
export const memoryExtractionTriggers = Object.freeze([
    "CONVERSATION_IDLE",
    "HANDOFF_RELEASED",
    "MANUAL_REPROCESS",
    "EVAL",
]);
export const memoryExtractionRunStatuses = Object.freeze([
    "QUEUED",
    "SUCCEEDED",
    "FAILED",
    "SKIPPED",
]);
export const memoryExtractionSkipReasons = Object.freeze([
    "NO_CUSTOMER_MESSAGES",
    "MEMORY_PAUSED",
    "EXTRACTION_OFF",
    "CUSTOMER_UNAVAILABLE",
    // CHT-771: the customer (or staff for the customer) deleted or corrected a fact. The messages up to that moment are never read again, so an
    // old message cannot teach the same fact back. Like a pause, this is a barrier.
    "MEMORY_CORRECTED",
]);
/**
 * The skip reasons that come from a control of the customer or from the state of the customer (CHT-1954 review F1). The range of such
 * a run is never read again: its end is a barrier, like the end of a SUCCEEDED run. A pause must stay a pause, so the messages sent
 * while memory was paused are not given to a model after the customer resumes. `EXTRACTION_OFF` is a setting of the operator and
 * `NO_CUSTOMER_MESSAGES` read the range already, so neither is a barrier.
 */
export const memoryExtractionCustomerControlSkipReasons = Object.freeze(["MEMORY_PAUSED", "CUSTOMER_UNAVAILABLE", "MEMORY_CORRECTED"]);
/** Who stood behind a fact. Ordered from the weakest to the strongest. */
export const memoryConfirmationLevels = Object.freeze([
    "UNCONFIRMED",
    "STAFF_CONFIRMED",
    "CUSTOMER_CONFIRMED",
]);
/** The stronger of two confirmation levels. A level only moves up. */
export function strongerConfirmationLevel(a, b) {
    return memoryConfirmationLevels.indexOf(a) >= memoryConfirmationLevels.indexOf(b) ? a : b;
}
/** The label every memory DTO carries: where the fact came from, derived from three stored columns. */
export const memoryProvenances = Object.freeze([
    "CUSTOMER_CONFIRMED",
    "STAFF_CONFIRMED",
    "CUSTOMER_STATED_IN_CHAT",
    "MODEL_INFERRED",
]);
/** Trust rank of a provenance label: a higher rank wins a conflict (design 3, rule 4). */
export const MEMORY_PROVENANCE_RANK = Object.freeze({
    CUSTOMER_CONFIRMED: 4,
    STAFF_CONFIRMED: 3,
    CUSTOMER_STATED_IN_CHAT: 2,
    MODEL_INFERRED: 1,
});
/**
 * Derives the provenance label (first match): the confirmation level, a legacy fact the customer wrote
 * themself, then a statement the extraction system captured from the customer's own words, then a fact a
 * staff member recorded. Everything else is a model inference (only a candidate can be in that state).
 * Confidence plays no part.
 */
export function deriveMemoryProvenance(fact) {
    if (fact.confirmationLevel === "CUSTOMER_CONFIRMED")
        return "CUSTOMER_CONFIRMED";
    if (fact.confirmationLevel === "STAFF_CONFIRMED")
        return "STAFF_CONFIRMED";
    // A legacy fact the customer wrote themself (rows from before Phase 5 carry no confirmation level).
    if (fact.policyVersion === null && fact.createdByActorKind === "customer") {
        return "CUSTOMER_CONFIRMED";
    }
    if (fact.createdByActorKind === "system" &&
        (fact.sourceType === "EXPLICIT_CUSTOMER_STATEMENT" ||
            fact.sourceType === "EXPLICIT_REMEMBER_REQUEST")) {
        return "CUSTOMER_STATED_IN_CHAT";
    }
    // A staff member who recorded a candidate stood behind it (a deliberate act, not a model reading).
    if (fact.createdByActorKind === "staff")
        return "STAFF_CONFIRMED";
    return "MODEL_INFERRED";
}
/**
 * The closed reasons a staff member gives for a memory command. A code, never free text, so a memory
 * value cannot be typed into `audit_events.reason` (closes trust-contract residual channel L7).
 */
export const memoryStaffReasonCodes = Object.freeze([
    "CUSTOMER_REQUESTED",
    "CUSTOMER_CONFIRMED_VERBALLY",
    "VERIFIED_FROM_TRIP",
    "INCORRECT_EXTRACTION",
    "OUTDATED",
    "DUPLICATE",
    "NOT_ALLOWED_SENSITIVE",
    "CONFLICT_RESOLVED",
    "OTHER",
]);
export const isMemoryStaffReasonCode = (value) => typeof value === "string" && memoryStaffReasonCodes.includes(value);
/**
 * What an extractor reports instead of a kind. The policy never writes any of these, at any status.
 * The first ten are sensitive; `CONTACT_OR_IDENTITY` and `OTHER` are unsupported (design 2.3).
 */
export const memoryUnsupportedClasses = Object.freeze([
    "HEALTH_CONDITION",
    "RELIGION_OR_BELIEF",
    "POLITICAL_VIEW",
    "SEXUAL_ORIENTATION",
    "ETHNICITY",
    "BIOMETRIC",
    "PRECISE_LOCATION",
    "PAYMENT_OR_FINANCIAL",
    "GOVERNMENT_ID",
    "CHILD_DETAIL",
    "CONTACT_OR_IDENTITY",
    "OTHER",
]);
export const memorySensitiveUnsupportedClasses = Object.freeze([
    "HEALTH_CONDITION",
    "RELIGION_OR_BELIEF",
    "POLITICAL_VIEW",
    "SEXUAL_ORIENTATION",
    "ETHNICITY",
    "BIOMETRIC",
    "PRECISE_LOCATION",
    "PAYMENT_OR_FINANCIAL",
    "GOVERNMENT_ID",
    "CHILD_DETAIL",
]);
/** A run decides at most this many candidates; the rest are counted as truncated (PROVISIONAL). */
export const MEMORY_EXTRACTION_MAX_CANDIDATES = 8;
// --- retention --------------------------------------------------------------------------------------------
/**
 * The longest a fact may live, in days, counted from the write (database `now()`). A `CANDIDATE` has one
 * ceiling for every kind. The database function `lafayette_memory_retention_ceiling` holds the same
 * numbers; a database test asserts the parity.
 */
export const MEMORY_RETENTION_V1 = Object.freeze({
    candidateDays: 30,
    activeDays: Object.freeze({
        DIETARY_PREFERENCE: 1095,
        DINING_STYLE: 730,
        PROPERTY_PREFERENCE: 730,
        SCHEDULE_PREFERENCE: 730,
        INTEREST: 730,
        DISLIKED_EXPERIENCE: 1095,
        TRAVEL_COMPANION_PATTERN: 365,
        EXPLICIT_INSTRUCTION: 730,
    }),
});
/** The ceiling in days for a fact of `kind` in `status` (a candidate has the short one; any other status the kind's). */
export function memoryRetentionCeilingDays(kind, status) {
    return status === "CANDIDATE"
        ? MEMORY_RETENTION_V1.candidateDays
        : MEMORY_RETENTION_V1.activeDays[kind];
}
const DAY_MS = 86_400_000;
/** `now` plus the ceiling. Calendar-free (a day is 24 hours), as the database interval of whole days is for a UTC session. */
export function memoryRetentionCeilingAt(kind, status, now) {
    return new Date(now.getTime() + memoryRetentionCeilingDays(kind, status) * DAY_MS);
}
const LABEL_KIND = {
    DIETARY_PREFERENCE: "dietary",
    DINING_STYLE: "dining_style",
    PROPERTY_PREFERENCE: "property",
    SCHEDULE_PREFERENCE: "schedule",
    INTEREST: "interest",
    DISLIKED_EXPERIENCE: "disliked",
    TRAVEL_COMPANION_PATTERN: "companion",
    EXPLICIT_INSTRUCTION: "instruction",
};
/** The i18n label key of a code. */
export const memoryLabelKey = (kind, code) => `memory.${LABEL_KIND[kind]}.${code}`;
function vocabulary(kind, codes) {
    return Object.freeze(codes.map((item) => {
        const [code, options] = typeof item === "string" ? [item, {}] : item;
        const personal = options.personal === true;
        return Object.freeze({
            code,
            sensitivity: personal ? "PERSONAL" : "STANDARD",
            autoPromotion: options.never === true
                ? "NEVER"
                : personal
                    ? "EXPLICIT_REMEMBER_ONLY"
                    : "EXPLICIT_STATEMENT",
            catalogTag: options.tag === undefined ? null : Object.freeze({ ...options.tag }),
            labelKey: memoryLabelKey(kind, code),
        });
    }));
}
const dietaryTag = (code) => ({ tag: { namespace: "DIETARY", code } });
const personalDietaryTag = (code) => ({
    personal: true,
    tag: { namespace: "DIETARY", code },
});
/**
 * The closed v1 vocabulary. The dietary codes that exist in the catalog `DIETARY` tag namespace carry
 * that tag, so a remembered value goes straight into the `dietary` filter of `searchCatalog`. Allergy
 * and religious-diet codes, and access needs, are PERSONAL and need an explicit remember request. Every
 * companion code is PERSONAL and is never auto-promoted.
 */
export const MEMORY_VOCABULARY_V1 = Object.freeze({
    DIETARY_PREFERENCE: vocabulary("DIETARY_PREFERENCE", [
        ["vegetarian", dietaryTag("vegetarian")],
        ["vegan", dietaryTag("vegan")],
        "pescatarian",
        ["gluten_free", dietaryTag("gluten_free")],
        "dairy_free",
        "no_pork",
        "no_beef",
        "no_alcohol",
        "low_spice",
        ["halal", personalDietaryTag("halal")],
        ["kosher", { personal: true }],
        ["jain", personalDietaryTag("jain")],
        ["nut_allergy", { personal: true }],
        ["peanut_allergy", { personal: true }],
        ["shellfish_allergy", { personal: true }],
        ["fish_allergy", { personal: true }],
        ["egg_allergy", { personal: true }],
        ["sesame_allergy", { personal: true }],
    ]),
    DINING_STYLE: vocabulary("DINING_STYLE", [
        "fine_dining",
        "casual",
        "tasting_menu",
        "chefs_table",
        "family_style",
        "street_food",
        ["quiet", { tag: { namespace: "AMBIENCE", code: "quiet" } }],
        ["lively", { tag: { namespace: "AMBIENCE", code: "lively" } }],
        ["romantic", { tag: { namespace: "AMBIENCE", code: "romantic" } }],
        "outdoor_seating",
        "private_room",
        "counter_seating",
    ]),
    PROPERTY_PREFERENCE: vocabulary("PROPERTY_PREFERENCE", [
        "quiet_room",
        "high_floor",
        "low_floor",
        ["sea_view", { tag: { namespace: "AMENITY", code: "sea_view" } }],
        "connecting_rooms",
        "twin_beds",
        "king_bed",
        ["private_pool", { tag: { namespace: "AMENITY", code: "private_pool" } }],
        "villa",
        "boutique_hotel",
        "large_hotel",
        "non_smoking",
        ["accessible_room", { personal: true }],
        [
            "step_free_access",
            { personal: true, tag: { namespace: "FEATURE", code: "step_free_access" } },
        ],
    ]),
    SCHEDULE_PREFERENCE: vocabulary("SCHEDULE_PREFERENCE", [
        "early_riser",
        "late_riser",
        "early_dinner",
        "late_dinner",
        "no_early_flights",
        "no_overnight_flights",
        "relaxed_pace",
        "full_days",
        "afternoon_rest",
    ]),
    INTEREST: vocabulary("INTEREST", [
        "modern_art",
        "classical_art",
        "architecture",
        "history",
        "wine",
        "cocktails",
        "food_markets",
        "live_jazz",
        "classical_music",
        "opera",
        "theatre",
        "fashion",
        "shopping",
        "hiking",
        "beaches",
        "wellness",
        "spa",
        "golf",
        "sailing",
        "wildlife",
        "photography",
        "design",
        "nightlife",
        "cooking_classes",
        "sport_events",
    ]),
    DISLIKED_EXPERIENCE: vocabulary("DISLIKED_EXPERIENCE", [
        "crowds",
        "long_queues",
        "tourist_spots",
        "early_starts",
        "long_drives",
        "boat_trips",
        "heights",
        "loud_venues",
    ]),
    TRAVEL_COMPANION_PATTERN: vocabulary("TRAVEL_COMPANION_PATTERN", [
        "solo",
        "partner",
        "family_with_children",
        "adult_family",
        "friends",
        "colleagues",
        "with_pet",
    ].map((code) => [code, { personal: true, never: true }])),
    EXPLICIT_INSTRUCTION: vocabulary("EXPLICIT_INSTRUCTION", [
        "window_seat",
        "aisle_seat",
        "confirm_before_booking",
        "private_transfers",
        "no_calls_before_10",
        "prefer_written_updates",
    ]),
});
/** The kinds whose value may also be a short free text (never auto-promoted). */
export const memoryTextKinds = Object.freeze([
    "DISLIKED_EXPERIENCE",
    "EXPLICIT_INSTRUCTION",
]);
const TEXT_MIN = 3;
const TEXT_MAX = 160;
const CODE_SHAPE = /^[a-z][a-z0-9_]{1,47}$/;
/**
 * A closed English list of terms that mark a free text as sensitive: diagnoses and medication, pregnancy,
 * religion, politics, sexuality, identity documents. A match refuses the text (`SENSITIVE_TEXT`). It is
 * a screen, not a classifier: the extractor also labels unsupported classes, and the database never
 * holds a SENSITIVE fact. PROVISIONAL, English only.
 */
export const MEMORY_SENSITIVE_TERMS_V1 = Object.freeze([
    // health
    "diabetes",
    "diabetic",
    "cancer",
    "hiv",
    "depression",
    "depressed",
    "anxiety",
    "bipolar",
    "schizophrenia",
    "asthma",
    "epilepsy",
    "epileptic",
    "dementia",
    "illness",
    "disease",
    "disorder",
    "diagnosis",
    "diagnosed",
    "medication",
    "medicine",
    "prescription",
    "chemotherapy",
    "surgery",
    "disability",
    "disabled",
    "pregnant",
    "pregnancy",
    "miscarriage",
    "psychiatrist",
    "mental health",
    // religion and belief
    "religion",
    "religious",
    "faith",
    "prayer",
    "catholic",
    "christian",
    "muslim",
    "islam",
    "jewish",
    "judaism",
    "hindu",
    "buddhist",
    "sikh",
    "atheist",
    // politics
    "politics",
    "political",
    "republican",
    "democrat",
    "conservative party",
    "labour party",
    "communist",
    "fascist",
    // sexuality and identity
    "gay",
    "lesbian",
    "bisexual",
    "transgender",
    "lgbt",
    "lgbtq",
    "sexual orientation",
    "ethnicity",
    // documents and money
    "passport",
    "visa number",
    "social security",
    "national id",
    "aadhaar",
    "credit card",
    "debit card",
    "bank account",
    "iban",
    "salary",
    "income",
]);
const SENSITIVE_TERM_PATTERNS = MEMORY_SENSITIVE_TERMS_V1.map((term) => new RegExp(`(?:^|[^a-z0-9])${term.replace(/ /g, "\\s+")}(?:$|[^a-z0-9])`));
/** Digits that make a text look like a phone, document or card number: this many or more (separators ignored). */
const SENSITIVE_DIGIT_COUNT = 9;
/** A format character (Unicode category Cf): zero-width, soft hyphen, bidi marks and tags. It is invisible, so it hides a word. */
const FORMAT_CHARACTER = /\p{Cf}/u;
const FORMAT_CHARACTERS = /\p{Cf}/gu;
/** True when the text holds a format or zero-width character. A memory value never does (CHT-718 review fix 3). */
export function hasMemoryFormatCharacter(text) {
    return FORMAT_CHARACTER.test(text) || FORMAT_CHARACTER.test(text.normalize("NFKC"));
}
/**
 * True when the text holds a sensitive term, or a run of nine or more digits (a phone, a document, a social security or a
 * card number: every Luhn-valid card number is such a run, so the screen is a superset of the design's "card numbers by
 * Luhn pattern"). Digits may be separated by one space, hyphen, dot or slash, as a card or a phone is written. An ISO date
 * holds eight digits and is not refused. The text is folded first: NFKC, every format and zero-width character removed
 * (so "diab\u200Betic" reads "diabetic"), then lower case.
 */
export function looksSensitiveMemoryText(text) {
    const folded = text.normalize("NFKC").replace(FORMAT_CHARACTERS, "").toLowerCase();
    if (SENSITIVE_TERM_PATTERNS.some((pattern) => pattern.test(folded)))
        return true;
    return (folded.match(/\d(?:[\s.\-/]?\d)+/g) ?? []).some((run) => run.replace(/[\s.\-/]/g, "").length >= SENSITIVE_DIGIT_COUNT);
}
/** A value as a code: lowercase, trimmed, spaces and hyphens become `_`. */
const asCode = (value) => value
    .normalize("NFKC")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
/** A value as a free text: NFKC, lowercase, whitespace collapsed. */
const asText = (value) => value.normalize("NFKC").toLowerCase().replace(/\s+/g, " ").trim();
/**
 * Normalizes a value and decides whether the kind accepts it, with its sensitivity and promotion class.
 * A CODE must be an entry of the kind's vocabulary (no fuzzy match here: the extractor maps to a code).
 * Only two kinds accept a TEXT (3 to 160 characters), and a TEXT is never auto-promoted. A value outside
 * the vocabulary that holds a sensitive term is `SENSITIVE_TEXT` in every kind. The result never holds
 * more than the normalized value, and a refusal holds only the reason.
 */
export function classifyMemoryValue(kind, value) {
    // An invisible character is never stored, in any kind (the database refuses it too). A sensitive word hidden by one is still
    // reported as sensitive. This check comes first: `trim` would otherwise drop a leading byte order mark.
    if (hasMemoryFormatCharacter(value)) {
        return {
            ok: false,
            reason: looksSensitiveMemoryText(value) ? "SENSITIVE_TEXT" : "TEXT_NOT_ALLOWED",
        };
    }
    const code = asCode(value);
    const entry = MEMORY_VOCABULARY_V1[kind].find((candidate) => candidate.code === code);
    if (entry !== undefined) {
        return {
            ok: true,
            form: "CODE",
            normalizedValue: entry.code,
            sensitivity: entry.sensitivity,
            autoPromotion: entry.autoPromotion,
            catalogTag: entry.catalogTag,
        };
    }
    if (looksSensitiveMemoryText(value))
        return { ok: false, reason: "SENSITIVE_TEXT" };
    if (!memoryTextKinds.includes(kind)) {
        return {
            ok: false,
            reason: CODE_SHAPE.test(code) ? "OUT_OF_VOCABULARY" : "TEXT_NOT_ALLOWED",
        };
    }
    const text = asText(value);
    const length = [...text].length;
    if (length > TEXT_MAX)
        return { ok: false, reason: "TEXT_TOO_LONG" };
    if (length < TEXT_MIN)
        return { ok: false, reason: "OUT_OF_VOCABULARY" };
    return {
        ok: true,
        form: "TEXT",
        normalizedValue: text,
        sensitivity: kind === "EXPLICIT_INSTRUCTION" ? "PERSONAL" : "STANDARD",
        autoPromotion: "NEVER",
        catalogTag: null,
    };
}
/** The sensitivity class of a classified value, as the closed `MemorySensitivity` type (the policy writes no SENSITIVE). */
export const memoryWritableSensitivities = Object.freeze([
    "STANDARD",
    "PERSONAL",
]);
/** Two different codes of one group contradict each other. PROVISIONAL. A group never spans two kinds (Ruling R6). */
export const MEMORY_CONFLICT_GROUPS_V1 = Object.freeze([
    { kind: "SCHEDULE_PREFERENCE", codes: ["early_riser", "late_riser"] },
    { kind: "SCHEDULE_PREFERENCE", codes: ["early_dinner", "late_dinner"] },
    { kind: "SCHEDULE_PREFERENCE", codes: ["relaxed_pace", "full_days"] },
    { kind: "PROPERTY_PREFERENCE", codes: ["high_floor", "low_floor"] },
    { kind: "PROPERTY_PREFERENCE", codes: ["twin_beds", "king_bed"] },
    { kind: "PROPERTY_PREFERENCE", codes: ["boutique_hotel", "large_hotel"] },
    { kind: "DINING_STYLE", codes: ["quiet", "lively"] },
    { kind: "EXPLICIT_INSTRUCTION", codes: ["window_seat", "aisle_seat"] },
    { kind: "DIETARY_PREFERENCE", codes: ["vegetarian", "pescatarian"] },
]);
/** A stricter value of the same need: `from` is the old code, `to` the stricter one. A refinement also counts as a conflict. */
export const MEMORY_REFINEMENT_PAIRS_V1 = Object.freeze([
    { kind: "DIETARY_PREFERENCE", from: "vegetarian", to: "vegan" },
    { kind: "DIETARY_PREFERENCE", from: "pescatarian", to: "vegetarian" },
    { kind: "DIETARY_PREFERENCE", from: "pescatarian", to: "vegan" },
]);
/**
 * Whether the new value contradicts the existing one: two different codes of one conflict group, or a refinement.
 * Only kinds with an overlapping vocabulary could conflict across kinds, and no code exists in both INTEREST and
 * DISLIKED_EXPERIENCE, so v1 has no cross-kind rule (Ruling R6). A loosening is no conflict: a `vegetarian` candidate
 * beside an ACTIVE `vegan` fact matches no group, so the ordinary decision table runs (Ruling R1).
 */
export function memoryValuesConflict(existing, incoming) {
    if (existing.kind !== incoming.kind)
        return false;
    if (existing.normalizedValue === incoming.normalizedValue)
        return false;
    return (MEMORY_CONFLICT_GROUPS_V1.some((group) => group.kind === incoming.kind &&
        group.codes.includes(existing.normalizedValue) &&
        group.codes.includes(incoming.normalizedValue)) || isMemoryRefinement(existing, incoming));
}
/** Whether `incoming` is a stricter value of the same need as `existing` (for example vegetarian to vegan). */
export function isMemoryRefinement(existing, incoming) {
    return MEMORY_REFINEMENT_PAIRS_V1.some((pair) => pair.kind === incoming.kind &&
        existing.kind === incoming.kind &&
        pair.from === existing.normalizedValue &&
        pair.to === incoming.normalizedValue);
}
